import React from "react";
import PropTypes from "prop-types";
import { Formik, Form } from "formik";
import withStyles from "components/with-styles";
import { FormattedMessage } from "react-intl";
import FormSubmit from "../form-submit";
import FormCancel from "../form-cancel";
import styles from "./styles/form.css.js";
import { useLoadingContext } from "components/loading-spinner-overlay";
import { injectIntl } from "react-intl";
import { DebouncingInputsProvider } from "../debouncing-inputs-context";
import {
  HTTP_ERROR_CODE_400,
  HTTP_ERROR_CODE_401,
  HTTP_ERROR_CODE_403,
  HTTP_ERROR_CODE_404,
  HTTP_ERROR_CODE_409,
  HTTP_ERROR_CODE_500,
  HTTP_MESSAGE_400,
  HTTP_MESSAGE_401,
  HTTP_MESSAGE_403,
  HTTP_MESSAGE_404,
  HTTP_MESSAGE_409,
  HTTP_MESSAGE_500,
} from "../../../utils/constants";

const PASSWORD_COMPLEXITY_ERROR_PATTERN = /password is not complex enough/i;

export const isPasswordComplexityError = message =>
  typeof message === "string" && PASSWORD_COMPLEXITY_ERROR_PATTERN.test(message);

const INVALID_IANA_TIMEZONE_ERROR_PATTERN = /^Invalid IANA timezone: (.+)$/i;

const getInvalidTimeZoneValue = message => {
  const match = typeof message === "string" && message.match(INVALID_IANA_TIMEZONE_ERROR_PATTERN);
  return match ? match[1] : null;
};

const getFormattedMessage = ({ intl, extensions }) => {
  try {
    if (extensions.data && Array.isArray(extensions.data)) {
      let completeData = "";
      extensions.data.forEach(data => {
        completeData += intl.formatMessage({ id: extensions.message + ".item" }, data) + extensions.dataSeparator;
      });
      return (
        intl.formatMessage({ id: extensions.message }) +
        completeData.substring(0, completeData.length - extensions.dataSeparator.length)
      );
    } else if (extensions.data) {
      return intl.formatMessage({ id: extensions.message }, extensions.data);
    }
    return intl.formatMessage({ id: extensions.message });
  } catch (e) {}
};

export const handleSubmitError = ({
  error,
  intl,
  setFieldError,
  values,
  genericErrorMessage,
  genericFileErrorMessage,
}) => {
  console.error(error);

  const { graphQLErrors } = error;
  const isFileUpload = values.image || values.file;

  if (error.response) {
    const { status, data } = error.response;

    let errorMessage = null;

    if (data) {
      if (typeof data === "string") {
        errorMessage = data;
      } else if (data.errors && typeof data.errors === "object") {
        const errorFields = Object.entries(data.errors)
          .map(([field, message]) => `${field}: ${message}`)
          .join(", ");
        errorMessage = data.message ? `${data.message} (${errorFields})` : errorFields;
      } else if (data.message) {
        errorMessage = data.message;
      } else if (data.error) {
        errorMessage = typeof data.error === "string" ? data.error : data.error.message;
      } else if (data.detail) {
        errorMessage = typeof data.detail === "string" ? data.detail : JSON.stringify(data.detail);
      } else if (data[status]) {
        errorMessage = data[status];
      } else if (data.collision_data) {
        errorMessage = intl.formatMessage({ id: "errors.event.conflict" }) || "Event conflict detected";
      }
    }

    if (errorMessage) {
      const invalidTimeZoneValue = getInvalidTimeZoneValue(errorMessage);

      if (invalidTimeZoneValue) {
        setFieldError(
          "place.timeZone",
          intl.formatMessage({ id: "errors.deployment.invalidTimeZone" }, { timeZone: invalidTimeZoneValue }),
        );
        return;
      }
    }

    if (errorMessage && isPasswordComplexityError(errorMessage)) {
      errorMessage = intl.formatMessage({ id: "errors.user.passwordNotComplex" });
    }

    if (errorMessage) {
      setFieldError("general", errorMessage);
      return;
    }

    if (status === HTTP_ERROR_CODE_400) {
      setFieldError("general", intl.formatMessage({ id: "common.error.badRequest" }) || HTTP_MESSAGE_400);
      return;
    } else if (status === HTTP_ERROR_CODE_401) {
      setFieldError("general", intl.formatMessage({ id: "common.error.unauthorized" }) || HTTP_MESSAGE_401);
      return;
    } else if (status === HTTP_ERROR_CODE_403) {
      setFieldError("general", intl.formatMessage({ id: "common.error.forbidden" }) || HTTP_MESSAGE_403);
      return;
    } else if (status === HTTP_ERROR_CODE_404) {
      setFieldError("general", intl.formatMessage({ id: "common.error.notFound" }) || HTTP_MESSAGE_404);
      return;
    } else if (status === HTTP_ERROR_CODE_409) {
      setFieldError("general", intl.formatMessage({ id: "common.error.conflict" }) || HTTP_MESSAGE_409);
      return;
    } else if (status >= HTTP_ERROR_CODE_500) {
      setFieldError("general", intl.formatMessage({ id: "common.error.server" }) || HTTP_MESSAGE_500);
      return;
    }
  }

  if (error.networkError && error.networkError.result) {
    const { result, statusCode } = error.networkError;
    const networkMessage =
      typeof result === "string"
        ? result
        : result[statusCode] || result.message || result?.errors?.[0]?.message;

    if (isPasswordComplexityError(networkMessage)) {
      setFieldError("general", intl.formatMessage({ id: "errors.user.passwordNotComplex" }));
      return;
    }
  }

  if (graphQLErrors && graphQLErrors.length > 0) {
    const { extensions, message } = graphQLErrors[0];

    if (isPasswordComplexityError(message) || isPasswordComplexityError(extensions?.message)) {
      setFieldError("general", intl.formatMessage({ id: "errors.user.passwordNotComplex" }));
      return;
    }

    if (extensions) {
      const formattedMessage = getFormattedMessage({ intl, extensions });
      setFieldError("general", formattedMessage || extensions.message || message || genericErrorMessage);
      return;
    } else if (message) {
      setFieldError("general", message);
      return;
    }
  }

  if (error.message && !error.response && !graphQLErrors) {
    if (error.message.includes("Network Error") || error.message.includes("timeout")) {
      setFieldError("general", intl.formatMessage({ id: "common.error.network" }));
      return;
    }

    if (error.message && error.message !== "Request failed") {
      setFieldError("general", error.message);
      return;
    }
  }

  setFieldError("general", isFileUpload ? genericFileErrorMessage : genericErrorMessage);
};

const flattenErrorPaths = (errors, prefix = "") =>
  Object.entries(errors || {}).flatMap(([key, value]) => {
    const path = prefix ? `${prefix}.${key}` : key;
    return value && typeof value === "object" ? flattenErrorPaths(value, path) : [`${path}: ${value}`];
  });

const ValidatedForm = ({
  initialValues,
  validationSchema,
  onSubmit,
  onSubmitError,
  children,
  classes,
  fullWidthSubmitButton,
  cancellable = true,
  intl,
  reloadOnNewInitialValues = true,
  onCancel,
  handleLoadingOverlay = true,
  validateOnChange = false,
  validateOnBlur = false,
  debouncingInputsValue,
  loadingControl = true,
}) => {
  const { setLoading } = useLoadingContext();
  const genericErrorMessage = intl.formatMessage({ id: "common.error" });
  const genericFileErrorMessage = intl.formatMessage({ id: "fileupload.error.generic.message" });

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={(values, { setSubmitting, setFieldError }) => {
        setSubmitting(true);
        if (handleLoadingOverlay) {
          setLoading(true);
        }

        Promise.resolve(onSubmit(values, setFieldError))
          .catch(error =>
            onSubmitError
              ? onSubmitError({
                  error,
                  intl,
                  setFieldError,
                  values,
                  genericErrorMessage,
                  genericFileErrorMessage,
                  defaultSubmitErrorHandler: handleSubmitError,
                })
              : handleSubmitError({
                  error,
                  intl,
                  setFieldError,
                  values,
                  genericErrorMessage,
                  genericFileErrorMessage,
                }),
          )
          .finally(() => {
            setSubmitting(false);
            if (handleLoadingOverlay && loadingControl) {
              setLoading(false);
            }
          });
      }}
      enableReinitialize={reloadOnNewInitialValues}
      validationSchema={validationSchema}
      validateOnBlur={validateOnBlur}
      validateOnChange={validateOnChange}
      render={({ handleSubmit, errors, isSubmitting, submitCount, ...props }) => {
        // eslint-disable-next-line no-console
        console.debug("Form errors", errors);

        const invalidFields = submitCount > 0 && !errors.general ? flattenErrorPaths(errors) : [];

        return (
          <Form onSubmit={handleSubmit}>
            <DebouncingInputsProvider value={debouncingInputsValue}>
              <div className={classes.form__serverError}>{errors.general}</div>
              <div className={classes.form__serverError}>{errors.generalYup}</div>
              {invalidFields.length > 0 && (
                <div className={classes.form__serverError}>
                  <FormattedMessage
                    id="common.form.invalidFields"
                    defaultMessage="El formulario tiene campos no válidos:"
                    values={{ fields: "" }}
                  />
                  <ul>
                    {invalidFields.map(field => (
                      <li key={field}>{field}</li>
                    ))}
                  </ul>
                </div>
              )}
              {children({ ...props })}
              <div className={classes.divider_bottom} />
              <div className={classes.button_container}>
                {cancellable && (
                  <FormCancel onClick={onCancel} fullWidth={fullWidthSubmitButton}>
                    <FormattedMessage id="common.cancel" />
                  </FormCancel>
                )}
                <FormSubmit disabled={isSubmitting} fullWidth={fullWidthSubmitButton}>
                  <FormattedMessage id="common.accept" />
                </FormSubmit>
              </div>
            </DebouncingInputsProvider>
          </Form>
        );
      }}
    />
  );
};

ValidatedForm.propTypes = {
  /**
   * Recarga el formulario cuando los valores iniciales cambian
   **/
  reloadOnNewInitialValues: PropTypes.bool,
  /**
   * Valores iniciales por defecto del formulario
   **/
  initialValues: PropTypes.object,
  /**
   * Esquema de validación del formulario (objeto yup)
   **/
  validationSchema: PropTypes.object,
  /**
   * Callback que se dispara al hacer submit del formulario
   **/
  onSubmit: PropTypes.func,
  /**
   * Callback que se dispara al producirse un error en el submit del formulario
   */
  onSubmitError: PropTypes.func,
  /**
   * Callback que se dispara al clicar el botón de cancelar
   **/
  onCancel: PropTypes.func,
  /**
   * Indica si los botones de submit y cancelar llevan los estilos fullWidth
   **/
  fullWidthSubmitButton: PropTypes.bool,
  /**
   * Indica que el formulario contiene el botón de cancelar, por defecto es true
   **/
  cancellable: PropTypes.bool,
  /**
   * Indica que el formulario handlea el overlay con spinner, por defecto es true
   **/
  handleLoadingOverlay: PropTypes.bool,
  /**
   * Valida el formulario completo al dispararse el evento onChange de alguno de los inputs, por defecto false
   **/
  validateOnChange: PropTypes.bool,
  /**
   * Valida el formulario completo al dispararse el evento onBlur de alguno de los inputs, por defecto false
   **/
  validateOnBlur: PropTypes.bool,
  /*
   * Indica el valor del debouncing para este formulario
   */
  debouncingInputsValue: PropTypes.number,
};

export default withStyles(styles)(injectIntl(ValidatedForm));
